import React, { useState } from 'react';
import Button from '../Button/Button';
import './LancerMarcheModal.css';
import { useModalData } from '../../hooks/useModalData';
import { useModalActions } from '../../hooks/useModalActions';
import ErreurPopUp from '../ErreurPopUp/ErreurPopUp';

interface LancerMarcheModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  showNomOperation?: boolean;
  showLotTechnique?: boolean;
  showLotGeographique?: boolean;
  showDepartement?: boolean;
  mode?: 'MS' | 'BC';
  expire_at?: string;
}

const LancerMarcheModal: React.FC<LancerMarcheModalProps> = ({
  isOpen,
  onClose,
  title = "Lancer un marché subséquent",
  showNomOperation = true,
  showLotTechnique = true,
  showLotGeographique = true,
  showDepartement = false,
  mode = 'MS'
}) => {
  const [formData, setFormData] = useState({
    nomOperation: '',
    lotTechnique: '',
    lotGeographique: '',
    departement: '',
    expire_at: '',

  });

  const [errors, setErrors] = useState<{
    nomOperation?: string;
    lotTechnique?: string;
    lotGeographique?: string;
    departement?: string;
  }>({});

  // États pour le modal d'erreur
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Utilisation des hooks personnalisés
  const {
    lotsTechniques,
    lotsGeographiques,
    departements,
    loading,
    error
  } = useModalData(isOpen, showLotTechnique, showLotGeographique, showDepartement);

  const { createOperation, isSubmitting } = useModalActions(onClose);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const validateForm = () => {
    const newErrors: typeof errors = {};

    if (showNomOperation && !formData.nomOperation.trim()) {
      newErrors.nomOperation = "Le nom de l'opération est requis";
    }

    if (showLotTechnique && !formData.lotTechnique) {
      newErrors.lotTechnique = "Le lot technique est requis";
    }

    if (showLotGeographique && !formData.lotGeographique) {
      newErrors.lotGeographique = "Le lot géographique est requis";
    }

    if (showDepartement && !formData.departement) {
      newErrors.departement = "Le département est requis";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      try {
        const expire_at = new Date(new Date().getTime() + 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 16);
        formData.expire_at = expire_at || '';
        await createOperation(formData, mode);
      } catch (error: any) {
        console.error('Erreur:', error);

        // Vérifier si c'est une erreur 400 avec un message
        if (error.status === 400 && error.message) {
          // Afficher le message dans le modal d'erreur
          setErrorMessage(error.message);
          setIsErrorModalOpen(true);
        } else {
          // Pour les autres erreurs, utiliser un message générique
          const errorMessage = error instanceof Error ? error.message :
            `Une erreur est survenue lors de la création du ${mode === 'BC' ? 'bon de commande' : 'marché subséquent'}.`;
          setErrorMessage(errorMessage);
          setIsErrorModalOpen(true);
        }
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="lancer-marche-modal">
        <h2>{title}</h2>

        {/* {loading && <div className="loading-message">Chargement...</div>} */}
        {error && <div className="error-message">Erreur: {error}</div>}

        <form onSubmit={handleSubmit}>
          {showNomOperation && (
            <div className="form-group">
              <label>Nom de l'opération <span className="required">*</span></label>
              <input
                type="text"
                name="nomOperation"
                value={formData.nomOperation}
                onChange={handleChange}
                className={errors.nomOperation ? 'error' : ''}
                placeholder="Nom de l'opération"
              />
              {errors.nomOperation && <span className="error-message">{errors.nomOperation}</span>}
            </div>
          )}

          {showLotTechnique && (
            <div className="form-group">
              <label>Lot technique <span className="required">*</span></label>
              <select
                name="lotTechnique"
                value={formData.lotTechnique}
                onChange={handleChange}
                className={errors.lotTechnique ? 'error' : ''}
              >
                <option value="">Sélectionnez un lot technique</option>
                {lotsTechniques.map(lot => (
                  <option key={lot.id} value={lot.id}>
                    {lot.code ? `${lot.code} - ` : ''}{lot.nom || lot.nom}
                  </option>
                ))}
              </select>
              {errors.lotTechnique && <span className="error-message">{errors.lotTechnique}</span>}
            </div>
          )}

          {showLotGeographique && (
            <div className="form-group">
              <label>Lot géographique <span className="required">*</span></label>
              <select
                name="lotGeographique"
                value={formData.lotGeographique}
                onChange={handleChange}
                className={errors.lotGeographique ? 'error' : ''}
              >
                <option value="">Sélectionnez un département</option>
                {lotsGeographiques.length > 0 ? (
                  lotsGeographiques.map(dep => (
                    <option key={dep.id} value={dep.id_departement.toString()}>
                      {dep.id_departement} - {dep.departement?.nom || 'Département'}
                    </option>
                  ))
                ) : (
                  <option value="">Aucun département disponible</option>
                )}
              </select>
              {errors.lotGeographique && <span className="error-message">{errors.lotGeographique}</span>}
            </div>
          )}

          {showDepartement && (
            <div className="form-group">
              <label>Département <span className="required">*</span></label>
              <select
                name="departement"
                value={formData.departement}
                onChange={handleChange}
                className={errors.departement ? 'error' : ''}
              >
                <option value="">Sélectionnez un département</option>
                {departements.length > 0 ? (
                  departements.map(dep => (
                    <option key={dep.id} value={dep.id}>
                      {dep.nom}
                    </option>
                  ))
                ) : (
                  <option value="">Aucun département disponible</option>
                )}
              </select>
              {errors.departement && <span className="error-message">{errors.departement}</span>}
            </div>
          )}

          <div className="modal-actions">
            <Button
              value="Annuler"
              variant="secondary"
              onClick={onClose}
              disabled={isSubmitting}
            />
            <Button
              value={isSubmitting ? "Création en cours..." : "Continuer"}
              variant="primary"
              type="submit"
              disabled={isSubmitting}
            />
          </div>
        </form>
      </div>

      {/* Modal d'erreur */}
      <ErreurPopUp
        isOpen={isErrorModalOpen}
        message={errorMessage}
        onClose={() => setIsErrorModalOpen(false)}
      />
    </div>
  );
};

export default LancerMarcheModal;